// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.⁠​‌‌‌​​​​​‌‌‌​​‌​​‌‌​‌​​‌​‌‌​‌‌​‌​‌‌​‌‌‌‌​‌‌‌​​‌​​‌‌​​‌​​​‌‌​‌​​‌​‌‌​​​​‌⁠
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 2dd2d19e-1b76-45ae-a20f-b359114b8796

import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Chip, IconButton,
  Accordion, AccordionSummary, AccordionDetails, TextField, MenuItem, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Alert, AlertTitle,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { FIELD_TYPES, PICKLISTS, resolvePicklist, relationMeta, TAG_GROUPS } from './schema';
import { TypeBadge, TagChips, CompletionBar, QualitySeal, LifecycleBar, fieldDisplay } from './ui';
import { printElementToPdf } from './exports';
import HistoryTab from './HistoryTab';
import RelationEditor from './RelationEditor';
import RelationsExplorer from './RelationsExplorer';
import IntegrationFlow from './IntegrationFlow';
import store from './store';
import HelpBubble from './HelpBubble';
import DeploymentTree from './DeploymentTree';
import AppNotesPanel from './AppNotesPanel';

// One editable field control.
function FieldEditor({ field, value, onChange }) {
  const common = { size: 'small', fullWidth: true, value: value ?? '', onChange: (e) => onChange(e.target.value) };
  switch (field.type) {
    case FIELD_TYPES.TEXTAREA:
      return <TextField {...common} multiline minRows={2} />;
    case FIELD_TYPES.SELECT:
    case FIELD_TYPES.RATING:
    case FIELD_TYPES.ARROWS:
      return (
        <TextField {...common} select>
          <MenuItem value=""><em>—</em></MenuItem>
          {resolvePicklist(field.picklist).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
      );
    case FIELD_TYPES.BOOLEAN:
      return <Switch checked={!!value} onChange={(e) => onChange(e.target.checked)} />;
    case FIELD_TYPES.NUMBER:
    case FIELD_TYPES.CURRENCY:
    case FIELD_TYPES.PERCENT:
      return <TextField {...common} type="number" onChange={(e) => onChange(Number(e.target.value))} />;
    default:
      return <TextField {...common} />;
  }
}

function Row({ label, help, children }) {
  return (
    <Box sx={{ display: 'flex', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ width: 260, color: '#666', fontSize: 13, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <span>{label}</span>
        {help}
      </Box>
      <Box sx={{ flex: 1, fontSize: 14 }}>{children}</Box>
    </Box>
  );
}

export default function FactSheetDetail({ id, tenantId, onBack, onOpen }) {
  const [fs, setFs] = useState(null);
  const [schema, setSchema] = useState(null);
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState({});
  const [tags, setTags] = useState([]);
  const [quality, setQuality] = useState('');
  const [integrations, setIntegrations] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneTarget, setCloneTarget] = useState('AWS');
  const [cutoverOpen, setCutoverOpen] = useState(false);
  const [cutoverCandidates, setCutoverCandidates] = useState([]);
  const [cutoverTargetId, setCutoverTargetId] = useState('');
  const [cuttingOver, setCuttingOver] = useState(false);
  const [cloneMode, setCloneMode] = useState('deployment');   // 'deployment' | 'copy'
  const [cloneName, setCloneName] = useState('');
  const [cloneEnv, setCloneEnv] = useState('Test');
  const [cloning, setCloning] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [printAll, setPrintAll] = useState(false);
  const [newField, setNewField] = useState({ label: '', type: FIELD_TYPES.TEXT, section: 'custom' });

  const reload = () => {
    setNotFound(false);
    store.getFactSheet(id).then((data) => {
      // A deleted or unknown id resolves null. `schema` was then never set, so
      // the guard below stayed true forever: a bare "Loading…" box with no back
      // button and no way out except the browser's back arrow.
      if (!data) { setNotFound(true); return; }
      setFs(data);
      setDraft((data && data.fields) || {});
      setTags((data && data.tags) || []);
      setQuality((data && data.quality) || 'Draft');
      setIntegrations((data && data.integrations) || []);
      store.schemaFor(data.tenantId, data.type).then(setSchema);
    }).catch(() => setNotFound(true));
  };
  useEffect(reload, [id]);

  if (notFound) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>This fact sheet is no longer available</AlertTitle>
          It was probably deleted, or the link points at a record in a workspace you are not in.
        </Alert>
        {onBack && <Button variant="outlined" onClick={onBack}>Back to the inventory</Button>}
      </Box>
    );
  }
  if (!fs || !schema) return <Box sx={{ p: 4 }}>Loading…</Box>;

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const save = async () => {
    await store.saveFactSheet({ ...fs, fields: draft, tags, quality, integrations });
    setEdit(false);
    reload();
  };
  const cancel = () => { setEdit(false); reload(); };
  // "This cannot be undone" stopped being true when 012_soft_delete.sql landed:
  // the record goes to a 30-day trash and an Optimum master can put it back.
  // Saying otherwise made people keep records they wanted rid of, which is the
  // opposite of the point of making deletion reversible.
  const removeSheet = async () => {
    if (!window.confirm(`Delete "${fs.name}"?\n\nIt goes to the trash for 30 days — ask Optimum if you need it back. After that it is gone for good.`)) return;
    await store.deleteFactSheet(fs.id);
    onBack();
  };

  // --- tag editing ---
  const setTag = (i, patch) => setTags((t) => t.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const addTag = () => setTags((t) => [...t, { group: 'Cell', value: '' }]);
  const delTag = (i) => setTags((t) => t.filter((_, j) => j !== i));

  // --- integration (data-flow) editing ---
  const setInt = (i, patch) => setIntegrations((a) => a.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const addInt = () => setIntegrations((a) => [...a, { to: '', via: '', protocol: '', port: '', direction: 'outbound', dataType: '' }]);
  const delInt = (i) => setIntegrations((a) => a.filter((_, j) => j !== i));
  // ---- Clone -------------------------------------------------------------
  // Values that identify THIS record rather than describe it. Copying them is
  // the trap: `importKey` is what a re-import matches on, so a clone carrying
  // the original's key would be updated by the next spreadsheet instead of the
  // original — or would merge the two. The AI markers are an audit trail of what
  // was drafted on the original and are not true of the copy.
  const NON_COPYABLE = ['importKey', 'importKeyStrategy', 'importSource',
    'aiAssisted', 'aiAssistedAt', 'aiAssistedFields', 'aiAssistedSource'];

  const copyableFields = (src) => {
    const out = { ...(src || {}) };
    NON_COPYABLE.forEach((k) => delete out[k]);
    return out;
  };

  const openClone = () => {
    const isApp = fs.type === 'Application';
    // A Deployment defaults to the MIGRATION clone rather than a plain copy,
    // because that is why anyone duplicates a live instance: they are standing
    // up its replacement on a different platform. A straight copy of a prod
    // deployment, with the same environment and lifecycle, is a second thing
    // claiming to be prod.
    const isDeployment = fs.type === 'Deployment';
    setCloneMode(isApp ? 'deployment' : (isDeployment ? 'migration' : 'copy'));
    setCloneEnv('Test');
    setCloneTarget('AWS');
    setCloneName(isApp ? `${fs.name} — Test`
      : (isDeployment ? `${fs.name} (AWS)` : `${fs.name} (copy)`));
    setCloneOpen(true);
  };

  // Keeps the suggested name in step with the platform, the same way pickEnv
  // does for environments, and only while the user has not typed their own.
  const pickTarget = (t) => {
    setCloneName((n) => (n === `${fs.name} (${cloneTarget})` || !n ? `${fs.name} (${t})` : n));
    setCloneTarget(t);
  };

  // THE CUTOVER. Two records change together: the new instance becomes the live
  // one, the old instance is marked decommissioned and keeps its history.
  //
  // Nothing is DELETED, which is the whole point of modelling it this way --
  // "we leave on prem intact". The old deployment stays queryable with its
  // servers, its cost line and its audit trail; it simply stops being Active.
  // A migration that erased the thing it replaced would destroy the before
  // side of every comparison the business case rests on.
  // Which other instances of the same application could this one be replacing?
  // Loaded when the dialog opens rather than stored as a relation: Frank's call
  // is that a clone is a duplicate, so the pair is not joined in the data. The
  // link exists only for the instant of the cutover, chosen by the person doing
  // it, and nothing carries it afterwards except the lifecycle states.
  //
  // Scoped to the same application AND the same tenant. Deployments are found
  // by walking the workspace, and CLAUDE.md's standing rule is that anything
  // keying fact sheets carries the tenant, because listFactSheets on the master
  // workspace returns every client's rows.
  const loadCutoverCandidates = async () => {
    const appId = ((fs.relations || {}).application || [])[0]?.id;
    const rows = await store.listFactSheets(fs.tenantId);
    const siblings = rows.filter((r) => r.type === 'Deployment'
      && r.id !== fs.id
      && r.tenantId === fs.tenantId
      && (!appId || ((r.relations || {}).application || []).some((a) => a.id === appId)));
    setCutoverCandidates(siblings);
    // Default to the live one when exactly one is live, which is the normal
    // case: one on-premises prod being replaced by one cloud prod.
    const live = siblings.filter((r) => r.fields?.lifecycleStatus === 'Active');
    setCutoverTargetId(live.length === 1 ? live[0].id : '');
  };

  const doCutover = async () => {
    setCuttingOver(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const predecessorId = cutoverTargetId;
      // The new instance first. If the second save fails, an Active new
      // instance beside an Active old one is a visible inconsistency someone
      // will fix; a Decommissioned old one beside a Planned new one is an
      // outage nobody can explain.
      await store.saveFactSheet({
        ...fs,
        fields: { ...fs.fields, lifecycleStatus: 'Active', goLiveDate: fs.fields?.goLiveDate || today },
      });
      if (predecessorId) {
        const prev = await store.getFactSheet(predecessorId);
        if (prev) {
          await store.saveFactSheet({
            ...prev,
            fields: {
              ...prev.fields,
              lifecycleStatus: 'Decommissioned',
              decommissionDate: prev.fields?.decommissionDate || today,
            },
          });
        }
      }
      setCutoverOpen(false);
      reload();
    } catch (e) {
      setCutoverOpen(false);
    } finally {
      setCuttingOver(false);
    }
  };

  // Keep the suggested name in step with the environment while the user has not
  // typed their own.
  const pickEnv = (env) => {
    const suggested = `${fs.name} — ${env}`;
    setCloneName((n) => (n === `${fs.name} — ${cloneEnv}` || !n ? suggested : n));
    setCloneEnv(env);
  };

  const doClone = async () => {
    const name = cloneName.trim();
    if (!name) return;
    setCloning(true);
    try {
      let record;
      if (cloneMode === 'migration') {
        // A DUPLICATE, deliberately, and not a linked pair. An earlier version
        // of this stored migratesTo/migratesFrom relations between the old and
        // new instance; Frank's call, 2026-08-19: "the cloning should
        // duplicate, not have a relationship."
        //
        // So the new instance is a full copy the user then edits: same fields,
        // same tags, same servers, same application. The servers ARE carried,
        // which is the visible difference from a target-state stub -- during a
        // lift and shift the new instance starts life looking exactly like the
        // old one, and the hosts are swapped as they are built rather than
        // attached to an empty record.
        //
        // The consequence, stated because it is real rather than hidden: until
        // those servers are repointed, both deployments reference the same
        // ITComponent rows, so a server count spanning both instances counts
        // them twice. That is the honest state of a migration in flight, and it
        // resolves as the new hosts replace the old ones.
        const src = copyableFields(fs.fields);
        record = {
          type: 'Deployment',
          tenantId: fs.tenantId,
          name,
          fields: {
            ...src,
            name,
            // Same environment: a prod instance is duplicated as a prod
            // instance. The platform and the name are what change.
            locationType: cloneTarget,
            lifecycleStatus: 'Planned',
            // The dates on the source describe the source. A duplicate must
            // not inherit a go-live or a decommission date it never had.
            goLiveDate: '',
            decommissionDate: '',
            description: src.description || `${cloneTarget} instance of ${fs.name}.`,
          },
          tags: (fs.tags || []).map((t) => ({ ...t })),
          relations: JSON.parse(JSON.stringify(fs.relations || {})),
          quality: 'Draft',
        };
      } else if (cloneMode === 'deployment') {
        // The modelled answer for prod/dev/test: ONE application, one Deployment
        // per instance. Servers, version and cutover date belong to the
        // instance, which is why cloning the Application instead would make the
        // estate unmigratable — you cut over Epic TST on a different date to
        // Epic PRD. Same shape Server Mapping creates, so the two agree.
        const src = copyableFields(fs.fields);
        record = {
          type: 'Deployment',
          tenantId: fs.tenantId,
          name,
          fields: {
            name,
            environment: cloneEnv,
            lifecycleState: src.lifecycleState || 'Active',
            // Carried because they genuinely describe an instance. Anything
            // portfolio-level (6R, TIME, criticality, business owner) stays on
            // the application and is deliberately not duplicated here.
            version: src.version || '',
            patchLevel: src.patchLevel || '',
            hostingType: src.hostingType || '',
            locationType: src.locationType || '',
            dataClassification: src.dataClassification,
            owner: src.technicalOwner || src.applicationOwner || '',
            description: `${cloneEnv} instance of ${fs.name}.`,
          },
          tags: [],
          relations: { application: [{ id: fs.id }] },
          quality: 'Draft',
        };
      } else {
        // A straight copy of the same type. Integrations are deliberately NOT
        // copied: a feed describes a real connection between two systems, and
        // duplicating them would double every count on the Interface Hub and
        // draw arrows that do not exist.
        record = {
          type: fs.type,
          tenantId: fs.tenantId,
          name,
          fields: { ...copyableFields(fs.fields), name },
          tags: (fs.tags || []).map((t) => ({ ...t })),
          relations: JSON.parse(JSON.stringify(fs.relations || {})),
          quality: 'Draft',
        };
      }
      const saved = await store.saveFactSheet(record);
      setCloneOpen(false);
      setCloning(false);
      if (saved && saved.id && onOpen) onOpen(saved.id);
    } catch (e) {
      setCloning(false);
    }
  };

  const addField = async () => {
    const key = 'cf_' + newField.label.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    await store.addFieldDef(fs.tenantId, fs.type, {
      key, label: newField.label, type: newField.type, section: newField.section,
    });
    setAddOpen(false);
    setNewField({ label: '', type: FIELD_TYPES.TEXT, section: 'custom' });
    reload();
  };

  const renderValue = (field) =>
    edit ? (
      <FieldEditor field={field} value={draft[field.key]} onChange={(v) => setField(field.key, v)} />
    ) : (
      fieldDisplay(field, fs.fields?.[field.key])
    );

  const renderRelations = (relKeys = []) =>
    relKeys.map((rk) => {
      const meta = relationMeta(rk);
      const items = fs.resolvedRelations?.[rk] || [];
      return (
        <Row key={rk} label={meta?.label || rk}>
          {items.length ? (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {items.map((it) => (
                <Chip key={it.id} size="small" icon={<Box sx={{ ml: 0.5 }}><TypeBadge type={it.type} size={16} /></Box>}
                  label={it.name} onClick={() => onOpen(it.id)} sx={{ cursor: 'pointer' }} />
              ))}
            </Stack>
          ) : (
            <span style={{ color: '#aaa' }}>—</span>
          )}
        </Row>
      );
    });

  return (
    <Box sx={{ p: 2, maxWidth: 1100, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <IconButton onClick={onBack}><ArrowBackIcon /></IconButton>
        <TypeBadge type={fs.type} size={28} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{fs.name}</Typography>
        <HelpBubble topic="factSheetDetail" />
        <Box sx={{ flex: 1 }} />
        <QualitySeal value={fs.quality} />
        {!edit ? (
          <Button startIcon={<EditIcon />} variant="outlined" onClick={() => setEdit(true)}>Edit</Button>
        ) : (
          <>
            <Button onClick={cancel}>Cancel</Button>
            <Button startIcon={<SaveIcon />} variant="contained" color="success" onClick={save}>Save</Button>
          </>
        )}
        {/* A collapsed MUI Accordion carries INLINE height:0;overflow:hidden, and
            inline styles are exactly what survives the innerHTML copy in
            exports.js — so every section the user had not expanded printed as
            nothing, with no indication it was omitted. Expand everything for the
            duration of the export, then put it back.
            The button also sat outside the `edit` ternary: exporting mid-edit
            produced a page of blank boxes, because React sets input values as
            DOM properties, not attributes. */}
        {/* Only on a deployment that was cloned FROM another and is not yet
            live. Anywhere else the button would either do nothing or promote
            an instance with no predecessor to decommission. */}
        {fs.type === 'Deployment' && fs.fields?.lifecycleStatus !== 'Active' && (
          <Tooltip title="Make this the live instance and mark the one it replaces as decommissioned">
            <span>
              <Button startIcon={<SwapHorizIcon />} variant="outlined" color="primary"
                disabled={edit} onClick={async () => { await loadCutoverCandidates(); setCutoverOpen(true); }}>
                Cut over
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip title={fs.type === 'Application'
          ? 'Add another environment (Dev, Test, Training…) or duplicate this fact sheet'
          : fs.type === 'Deployment'
            ? 'Clone this instance to AWS or Azure as its target state'
            : 'Duplicate this fact sheet'}>
          <span>
            <Button startIcon={<ContentCopyIcon />} variant="outlined" disabled={edit} onClick={openClone}>
              Clone
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={edit ? 'Save or cancel before exporting — an in-progress edit prints as blank boxes' : 'Export to PDF'}>
          <span>
            <Button
              startIcon={<PictureAsPdfIcon />}
              variant="outlined"
              disabled={edit}
              onClick={() => {
                setPrintAll(true);
                // let the accordions paint open before the DOM is copied
                setTimeout(() => {
                  printElementToPdf('fs-print', fs.name);
                  setPrintAll(false);
                }, 60);
              }}
            >
              PDF
            </Button>
          </span>
        </Tooltip>
        {edit && (
          <Tooltip title="Delete this fact sheet">
            <Button color="error" variant="outlined" onClick={removeSheet}>Delete</Button>
          </Tooltip>
        )}
      </Stack>

      {/* Tags + quality seal are editable like everything else. */}
      {!edit ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
          <TagChips tags={fs.tags} />
          <Box sx={{ flex: 1 }} />
          <CompletionBar value={fs.completion} width={180} />
        </Stack>
      ) : (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <Typography variant="overline" sx={{ color: 'sectionHeader.main', fontWeight: 700 }}>Tags</Typography>
            <TextField select size="small" label="Quality seal" value={quality}
              onChange={(e) => setQuality(e.target.value)} sx={{ minWidth: 160 }}>
              {resolvePicklist('qualitySeal').map((q) => <MenuItem key={q} value={q}>{q}</MenuItem>)}
            </TextField>
            <Box sx={{ flex: 1 }} />
            <Button size="small" startIcon={<AddIcon />} onClick={addTag}>Add tag</Button>
          </Stack>
          {tags.map((t, i) => (
            <Stack key={i} direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
              <TextField select size="small" label="Group" value={t.group}
                onChange={(e) => setTag(i, { group: e.target.value })} sx={{ minWidth: 150 }}>
                {TAG_GROUPS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
              </TextField>
              <TextField size="small" label="Value" value={t.value} sx={{ flex: 1 }}
                onChange={(e) => setTag(i, { value: e.target.value })} />
              <IconButton size="small" onClick={() => delTag(i)}><DeleteIcon fontSize="small" /></IconButton>
            </Stack>
          ))}
        </Paper>
      )}

      {/* Relations Explorer graph */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="overline" sx={{ color: 'sectionHeader.main', fontWeight: 700 }}>Relations Explorer</Typography>
        <RelationsExplorer fs={fs} onOpen={onOpen} />
      </Paper>

      {/* Printable content */}
      <div id="fs-print">
        <h1>{fs.name}</h1>
        <Accordion defaultExpanded={(fs.integrations || []).length > 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>
              Integrations & Data Flow {fs.integrations?.length ? `(${fs.integrations.length})` : ''}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {!edit ? (
              <IntegrationFlow appName={fs.name} integrations={fs.integrations || []} />
            ) : (
              <Box>
                {integrations.map((it, i) => (
                  <Stack key={i} direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center" flexWrap="wrap">
                    <TextField size="small" label="Connects to" value={it.to || ''} sx={{ minWidth: 170 }}
                      onChange={(e) => setInt(i, { to: e.target.value })} />
                    <TextField size="small" label="Via (engine)" value={it.via || ''} sx={{ minWidth: 170 }}
                      onChange={(e) => setInt(i, { via: e.target.value })} />
                    <TextField size="small" label="Protocol" value={it.protocol || ''} sx={{ minWidth: 130 }}
                      onChange={(e) => setInt(i, { protocol: e.target.value })} />
                    <TextField size="small" label="Port(s)" value={it.port || ''} sx={{ minWidth: 120 }}
                      onChange={(e) => setInt(i, { port: e.target.value })} />
                    <TextField select size="small" label="Direction" value={it.direction || 'outbound'} sx={{ minWidth: 140 }}
                      onChange={(e) => setInt(i, { direction: e.target.value })}>
                      {['outbound', 'inbound', 'bidirectional'].map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                    </TextField>
                    <TextField size="small" label="Data" value={it.dataType || ''} sx={{ minWidth: 140 }}
                      onChange={(e) => setInt(i, { dataType: e.target.value })} />
                    <IconButton size="small" onClick={() => delInt(i)}><DeleteIcon fontSize="small" /></IconButton>
                  </Stack>
                ))}
                <Button size="small" startIcon={<AddIcon />} onClick={addInt}>Add integration</Button>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
        {schema.sections.map((section) => (
          <Accordion
            key={section.key}
            defaultExpanded={['information', 'classification', 'migrationKnowledge', 'portfolioStrategy'].includes(section.key)}
            {...(printAll ? { expanded: true } : {})}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>{section.label}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {/* Lifecycle bar for the information section */}
              {section.key === 'information' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#666' }}>Lifecycle</Typography>
                  <LifecycleBar from={fs.fields?.lifecycle} state={fs.fields?.lifecycleState} />
                </Box>
              )}
              {(section.fields || []).map((f) => (
                <Row key={f.key} label={f.label + (f.mandatory ? ' *' : '')} help={<HelpBubble topic={f.key} />}>
                  {renderValue(f)}
                </Row>
              ))}
              {renderRelations(section.relations)}

              {/* The flat chip rows above list Deployments as siblings of every
                  other relation, which loses the one thing that makes them
                  useful: what runs underneath each stage. Rendered only on an
                  Application, and only in the section that owns the deployments
                  relation, so it appears exactly once. */}
              {fs.type === 'Application' && (section.relations || []).includes('deployments')
                && <DeploymentTree factSheet={fs} onOpen={onOpen} />}

              {/* Notes and supporting documents ride in the same section as the
                  deployment tree because they are the same act: sitting with a
                  client, going through an application, writing down what was
                  said and attaching what was handed over. Rendered once, on
                  Applications only. */}
              {fs.type === 'Application' && (section.relations || []).includes('deployments') && (
                <Box sx={{ mt: 3 }}>
                  <AppNotesPanel factSheetId={fs.id} tenantId={fs.tenantId} canEdit={edit} />
                </Box>
              )}

              {/* Cloud Transformation subsections with per-team ownership + % complete */}
              {(section.subsections || []).map((sub) => (
                <Box key={sub.key} sx={{ mt: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{sub.label}</Typography>
                    <Chip size="small" variant="outlined" label={sub.team} sx={{ height: 18, fontSize: 10 }} />
                    <Box sx={{ flex: 1 }} />
                    {sub.complete && <CompletionBar value={fs.fields?.[sub.complete] || 0} width={140} />}
                  </Stack>
                  {(sub.fields || []).map((f) => (
                    <Row key={f.key} label={f.label} help={<HelpBubble topic={f.key} />}>{renderValue(f)}</Row>
                  ))}
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </div>

      {/* Relationships, including the fields that live ON each connection */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>Relationships &amp; dependencies</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <RelationEditor factSheet={fs} onSaved={reload} />
        </AccordionDetails>
      </Accordion>

      {/* Change history — who edited what, from the audit trail */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>Change history</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <HistoryTab factSheetId={fs.id} tenantId={fs.tenantId} />
        </AccordionDetails>
      </Accordion>

      {/* Add custom field — the flexible-schema superpower */}
      <Button startIcon={<AddIcon />} sx={{ mt: 2 }} onClick={() => setAddOpen(true)}>
        Add custom field to {fs.type}
      </Button>

      <Dialog open={cloneOpen} onClose={() => !cloning && setCloneOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Clone {fs.name}</DialogTitle>
        <DialogContent>
          {fs.type === 'Application' && (
            <>
              <TextField
                select fullWidth size="small" label="What are you creating?" sx={{ mt: 1, mb: 2 }}
                value={cloneMode} onChange={(e) => setCloneMode(e.target.value)}
              >
                <MenuItem value="deployment">Another environment of this application</MenuItem>
                <MenuItem value="copy">A separate application, prefilled from this one</MenuItem>
              </TextField>
              <Alert severity="info" sx={{ mb: 2 }}>
                {cloneMode === 'deployment'
                  ? 'Creates a Deployment linked to this application. Prod, Test and Training are instances of ONE application — servers, version and cutover date belong to the instance, which is what lets you cut them over on different dates.'
                  : 'Creates a second application. Use this only when it really is a different product — for Dev/Test/Prod of the same one, choose the environment option above.'}
              </Alert>
            </>
          )}
          {cloneMode === 'migration' && (
            <TextField
              select fullWidth size="small" label="Target platform" sx={{ mb: 2 }}
              value={cloneTarget} onChange={(e) => pickTarget(e.target.value)}
            >
              {['AWS', 'Azure', 'Co-location', 'Vendor Cloud (SaaS)'].map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </TextField>
          )}
          {cloneMode === 'deployment' && (
            <TextField
              select fullWidth size="small" label="Environment" sx={{ mb: 2 }}
              value={cloneEnv} onChange={(e) => pickEnv(e.target.value)}
            >
              {(PICKLISTS.environment || []).map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
            </TextField>
          )}
          <TextField
            fullWidth size="small" label="Name" value={cloneName}
            onChange={(e) => setCloneName(e.target.value)}
          />
          <Typography variant="caption" sx={{ display: 'block', color: '#5C6B7A', mt: 1.5 }}>
            {cloneMode === 'migration'
              ? 'The on-premises instance is left exactly as it is. The new one starts as Planned and carries NO servers — the point of the move is that it gets new hosts, and copying the old list would double every server count. Cut over from the new instance once it is live.'
              : cloneMode === 'deployment'
                ? 'Version, patch level, hosting and owner are carried over. Portfolio decisions — 6R, TIME, criticality, business owner — stay on the application and are not duplicated.'
                : 'Fields and tags are copied. Integrations are not: a feed describes a real connection, and copying it would double every count on the Interface Hub.'}
            {' '}The import key is never copied, so a future spreadsheet still matches the original.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneOpen(false)} disabled={cloning}>Cancel</Button>
          <Button variant="contained" onClick={doClone} disabled={cloning || !cloneName.trim()}>
            {cloning ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cutoverOpen} onClose={() => !cuttingOver && setCutoverOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>
          Cut over to this instance <HelpBubble topic="deploymentCutover" />
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>{fs.name}</strong> becomes the live instance. Choose the instance it
            replaces and that one is marked <strong>Decommissioned</strong> with
            today&rsquo;s date.
          </Typography>
          {/* Picked here rather than read from a stored relation: a clone is a
              duplicate, so the two records are not joined in the data. Whoever
              performs the cutover says which instance is being retired. */}
          <TextField
            select fullWidth size="small" label="Instance being replaced" sx={{ mb: 2 }}
            value={cutoverTargetId}
            onChange={(e) => setCutoverTargetId(e.target.value)}
            helperText={cutoverCandidates.length
              ? 'Other instances of this application in this workspace.'
              : 'No other instance of this application to retire. This one is simply made live.'}
          >
            <MenuItem value=""><em>Nothing &mdash; just make this one live</em></MenuItem>
            {cutoverCandidates.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
                {c.fields?.lifecycleStatus ? ` — ${c.fields.lifecycleStatus}` : ''}
              </MenuItem>
            ))}
          </TextField>
          {/* Said plainly, because "decommissioned" reads like "deleted" to
              most people and the whole design depends on it not being. */}
          <Alert severity="info">
            Nothing is deleted. The instance being replaced keeps its servers, its history and
            its cost line, and stays open for reporting. It simply stops being the live one,
            which is what makes a before-and-after comparison possible at all.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCutoverOpen(false)} disabled={cuttingOver}>Cancel</Button>
          <Button variant="contained" onClick={doCutover} disabled={cuttingOver}>
            {cuttingOver ? 'Cutting over…' : 'Cut over'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add custom field</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" label="Field label" sx={{ mt: 1, mb: 2 }}
            value={newField.label} onChange={(e) => setNewField({ ...newField, label: e.target.value })} />
          <TextField select fullWidth size="small" label="Type"
            value={newField.type} onChange={(e) => setNewField({ ...newField, type: e.target.value })}>
            {[FIELD_TYPES.TEXT, FIELD_TYPES.TEXTAREA, FIELD_TYPES.NUMBER, FIELD_TYPES.CURRENCY, FIELD_TYPES.BOOLEAN, FIELD_TYPES.DATE, FIELD_TYPES.URL].map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>
          {/* Which area the field joins. Without this every custom field landed
              in one "Custom Fields" pile, so the obvious way to deepen an
              existing area -- add a field to Ownership, or to Migration
              Knowledge -- put it somewhere else entirely. */}
          <TextField select fullWidth size="small" label="Section" sx={{ mt: 2 }}
            value={newField.section}
            onChange={(e) => setNewField({ ...newField, section: e.target.value })}>
            {schema.sections.map((sec) => (
              <MenuItem key={sec.key} value={sec.key}>{sec.label}</MenuItem>
            ))}
            {!schema.sections.some((sec) => sec.key === 'custom') && (
              <MenuItem value="custom">Custom Fields</MenuItem>
            )}
          </TextField>
          <Typography variant="caption" sx={{ color: '#888', mt: 1, display: 'block' }}>
            Applies to all {fs.type} fact sheets in this workspace. (Per-tenant schema — stored as a field definition.)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!newField.label} onClick={addField}>Add field</Button>
        </DialogActions>
      </Dialog>
      {/* A second Save at the foot. The only one was in the header, and a fact
          sheet is long enough that finishing an edit meant scrolling back up to
          commit it -- which is exactly where unsaved work gets abandoned.
          Reuses cancel/save, so the two controls cannot drift apart. */}
      {edit && (
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3, mb: 1 }}>
          <Button onClick={cancel}>Cancel</Button>
          <Button startIcon={<SaveIcon />} variant="contained" color="success" onClick={save}>Save</Button>
        </Stack>
      )}
    </Box>
  );
}
